import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  Input,
  notification,
} from 'antd';
import * as Auth from 'aws-amplify/auth';
import { ConsoleLogger as Logger } from 'aws-amplify/utils';
import { withTranslation } from 'react-i18next';

import { generateNotificationFromCognitoError } from '../../utils/errorHelpers';

import './index.less';

const logger = new Logger('new-password:form');

const NewPasswordForm = (props) => {
  const { t, email, isPasswordReset } = props;
  const [isLoading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleOnFinish = async (body) => {
    setLoading(true);
    try {
      const { newPassword, verificationCode } = body;

      await Auth.confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword,
      });

      isPasswordReset(true);
    } catch (error) {
      logger.error(error);

      const { code, message } = error;
      const passwordError = {};

      if (code === 'InvalidPasswordException') {
        switch (message) {
          case 'Password does not conform to policy: Password must have uppercase characters':
            passwordError.code = 'PasswordMissingUppercaseCharsException';
            break;
          case 'Password does not conform to policy: Password must have numeric characters':
            passwordError.code = 'PasswordMissingNumericCharsException';
            break;
          case 'Password does not conform to policy: Password not long enough':
            passwordError.code = 'PasswordTooShortException';
            break;
          case 'Password does not conform to policy: Password must have symbol characters':
            passwordError.code = 'PasswordMustHaveSymbolsException';
            break;
          default: // noop
        }
      }

      const notificationMessage = generateNotificationFromCognitoError(t, {
        ...error,
        ...passwordError,
      });
      notification.error(notificationMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form
        className="forgot-password-form"
        onFinish={handleOnFinish}
        form={form}
        name="new-password"
      >
        <Form.Item
          label={t('forgotPassword.form.label.verificationCode')}
          colon={false}
          name="verificationCode"
          rules={[
            {
              required: true,
              message: t('common.form.emptyInputFieldErrorMessage'),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t('forgotPassword.form.label.newPassword')}
          colon={false}
          name="newPassword"
          rules={[
            {
              required: true,
              message: t('common.form.emptyInputFieldErrorMessage'),
            },
            {
              required: true,
              min: 8,
              message: t('common.form.passwordNotLongEnoughMessage'),
            },
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          label={t('forgotPassword.form.label.newPasswordAgain')}
          colon={false}
          name="newPasswordAgain"
          rules={[
            {
              required: true,
              message: t('common.form.emptyInputFieldErrorMessage'),
            },
            {
              required: true,
              validator: async (rule, value) => (value === form.getFieldValue('newPassword')
                ? Promise.resolve()
                : Promise.reject(
                  t('forgotPassword.form.message.passwordsMatchRequired'),
                )),
              message: t('forgotPassword.form.message.passwordsMatchRequired'),
            },
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item className="submit-button">
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            data-testid="submit-button"
          >
            {isLoading ? t('common.status.sending') : t('common.button.submit')}
          </Button>
        </Form.Item>
      </Form>
      <p className="new-password__login-container">
        <a href="/">{t('common.button.backToSignIn')}</a>
      </p>
    </>
  );
};

NewPasswordForm.displayName = 'NewPasswordForm';
NewPasswordForm.propTypes = {
  t: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  isPasswordReset: PropTypes.func.isRequired,
};

export default withTranslation()(NewPasswordForm);
export {
  NewPasswordForm as PureComponent,
};
